import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AngleProfileCard from "./AngleProfileCard";

/**
 * Launcher for Residual vs Angle, at the top of the settings rail, opening the chart in a modal.
 *
 * It used to sit inline at the bottom of the chart column, which had two problems. It was below
 * the fold, so the one analysis that has to be deliberately run was the hardest thing to find.
 * And its x-axis is polar ANGLE about each circle's own centre, while every chart above it is
 * position along the shaft - stacked in one column they invite reading a feature at "x = 40" on
 * one as related to "40°" on another. They are unrelated axes, and a modal makes that structural
 * rather than something a caption has to talk the reader out of.
 */
// Chart is 300px; the header, legend, the usable-arc and clip-window readouts and the hover row
// account for the rest. Measured against the loaded-with-hover state, which is the tallest.
const DIALOG_CONTENT_HEIGHT = 496;

export default function AngleProfileDialog() {
    const [open, setOpen] = useState(false);
    const rawSurface = useSelector((state) => state.rawSurface);
    const ready = Boolean(rawSurface.captureId);

    return (
        <>
            <Tooltip
                title={ready
                    ? "Pooled signed residual against polar angle, across every circle in the capture. Measures where the specular nipple sits and how far out the arc stays usable."
                    : "Load a capture first."}
            >
                <span>
                    <Button
                        fullWidth
                        variant="outlined"
                        disabled={!ready}
                        onClick={() => setOpen(true)}
                    >
                        Residual vs Angle
                    </Button>
                </span>
            </Tooltip>

            {/* Kept mounted only while open: the chart holds a canvas and its own hover state,
                and there is no reason for either to exist while the dialog is shut. The profile
                itself lives in Redux, so closing and reopening does not re-run the analysis. */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <span>Residual vs Angle</span>
                        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Polar angle about each circle&rsquo;s own fitted centre &mdash; 0&deg; is the
                        point closest to the laser. Not the same axis as the charts behind this.
                    </Typography>
                    {/* Stated rather than left to be discovered: the chart is hand-drawn, so it
                        carries none of the affordances a Chart.js plot would. */}
                    <Typography variant="caption" color="text.secondary" display="block">
                        Drag to pan &middot; Ctrl-scroll or pinch to zoom &middot; double-click to
                        reset
                    </Typography>
                </DialogTitle>
                {/* One fixed height, deliberately. The card has three content states - before
                    the run, after it, and with the pointer over the plot - and letting the dialog
                    size to each made it jump vertically on every transition. Pinned to the tallest
                    state; overflow scrolls instead of resizing, so a narrow window that wraps the
                    readouts still cannot change the dialog's height. */}
                <DialogContent
                    sx={{
                        height: DIALOG_CONTENT_HEIGHT,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <AngleProfileCard bare />
                </DialogContent>
            </Dialog>
        </>
    );
}
